import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowRight, Info } from "lucide-react";
import { api, NAME_TO_SLUG } from "@/lib/api";
import { sanitizeHtml } from "@/lib/sanitize";
import { pickTool } from "@/lib/toolLinks";
import Seo from "@/components/Seo";
import ScoutBox from "@/components/ScoutBox";
import ArticleCard from "@/components/ArticleCard";
import { productImg } from "@/lib/img";
import NewsletterSignup from "@/components/NewsletterSignup";
import ChalkosCTA from "@/components/ChalkosCTA";
import { ArticleOffer } from "@/components/ArticleOffer";

export default function Article() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    (async () => {
      try {
        const { data } = await api.get(`/articles/${slug}`);
        setArticle(data);
        const r = await api.get(`/articles?category=${encodeURIComponent(data.category)}&limit=4`);
        setRelated((r.data || []).filter((x) => x.slug !== slug).slice(0, 3));
      } catch {
        setNotFound(true);
      }
    })();
  }, [slug]);

  if (notFound) return <div className="max-w-3xl mx-auto px-4 py-24 text-center text-[#6E6E6E]">Article not found. <Link to="/" className="link-line text-[#2D2D2D]">Home</Link></div>;
  if (!article) return <div className="max-w-3xl mx-auto px-4 py-24 text-[#6E6E6E]">Loading…</div>;

  const date = new Date(article.date).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  const tool = pickTool(article);

  return (
    <article className="pt-10" data-testid="article-page">
      <Seo title={article.title} description={article.excerpt} path={`/article/${article.slug}`}
           image={article.image_url} type="article" article={article} />
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <Link to={`/category/${NAME_TO_SLUG[article.category]}`} className="inline-block bg-[#2D2D2D] text-[#F3F1EC] font-head uppercase tracking-wide text-xs px-2.5 py-1" data-testid="article-category-badge">
          {article.category}
        </Link>
        <h1 className="mt-5 font-head font-semibold uppercase leading-[1.05] text-3xl lg:text-5xl text-[#1E1E1E]">{article.title}</h1>
        <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-[#6E6E6E] font-head uppercase tracking-wide">
          <span>{article.byline}</span>
          <span className="text-[#D3CFC0]">•</span>
          <span>{date}</span>
          <span className="text-[#D3CFC0]">•</span>
          <span>{article.reading_time} min read</span>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 mt-8">
        <div className="aspect-[16/8] overflow-hidden border-2 border-[#1E1E1E]">
          <img {...productImg(article.image_url, "(min-width: 896px) 896px, 100vw")} alt={`${article.title} — Voluntold ${article.category}`} fetchpriority="high" className="w-full h-full object-cover" />
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 mt-10">
        {article.flagged_for_review && (
          <div className="mb-6 flex items-start gap-2 border border-[#D3CFC0] bg-[#E7E3D9] p-3 text-xs text-[#6E6E6E]" data-testid="review-flag">
            <Info size={15} className="mt-0.5 shrink-0 text-[#2D2D2D]" />
            <span>This piece touches on athlete wellbeing. It's under a manual human-review pass — read it as a starting point for a conversation, not a diagnosis.</span>
          </div>
        )}

        <div className="article-body" dangerouslySetInnerHTML={{ __html: sanitizeHtml(article.body_html) }} data-testid="article-body" />

        <p className="mt-8 border-l-4 border-[#E8A33D] bg-[#E7E3D9] px-4 py-3 text-[15px] leading-relaxed text-[#1E1E1E]" data-testid="article-tool-link">
          Put this into practice: try the{" "}
          <Link to={tool.path} className="link-line text-[#2D2D2D] font-semibold">{tool.name}</Link>{" "}— {tool.blurb}.
        </p>

        {/* ChalkOS module — renders only on reviewed Strong-fit (operational) articles */}
        <ChalkosCTA article={article} placement="article-end" />

        <ArticleOffer slug={article.slug} />

        <div className="mt-10">
          <ScoutBox testId="article-scout-box">
            Working on this exact area? Scout maps it to a next step — see{" "}
            <Link to="/assessment" className="link-line text-[#2D2D2D]">where you stand</Link>, or browse{" "}
            <Link to="/products" className="link-line text-[#2D2D2D]">the digital products</Link> built for coaches and parents.
          </ScoutBox>
        </div>

        <div className="mt-8 border-t border-[#D3CFC0] pt-6 flex items-center gap-4">
          <div className="w-12 h-12 bg-[#2D2D2D] text-[#F3F1EC] font-head text-xl flex items-center justify-center">K</div>
          <div className="text-sm text-[#6E6E6E]">
            <span className="font-head uppercase tracking-wide text-[#1E1E1E]">Coach Kyto, CSPC</span><br />
            20 years of coaching on top of 20 years of playing · CSPC-certified in mental performance.
          </div>
        </div>

        <p className="mt-6 text-xs leading-relaxed text-[#8A8578] italic" data-testid="article-ai-disclaimer">
          A note on how this is made: some Voluntold articles are drafted with the help of AI and then shaped to match Coach Kyto's voice and experience. It's education from the sideline — not medical, mental-health, or clinical advice.
        </p>

        <div className="mt-10 border-2 border-[#222222] bg-[#222222] p-6 sm:p-8" data-testid="article-newsletter">
          <span className="font-head uppercase tracking-[0.2em] text-xs text-[#DB9130]">One coaching read a week</span>
          <h3 className="mt-2 font-head font-semibold uppercase text-xl text-[#F5F0EA]">Youth sports, from both sides of the whistle.</h3>
          <p className="mt-2 text-[#A9B6BF] text-sm leading-relaxed max-w-lg">
            Get one worthwhile read each week on coaches, players, parents, and everything happening between them.
          </p>
          <div className="mt-4">
            <NewsletterSignup source="article" tone="dark" showCopy={false} testId="article-newsletter-signup" />
          </div>
        </div>
      </div>

      {related.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-20">
          <h2 className="font-head font-semibold uppercase text-2xl text-[#1E1E1E] border-b-2 border-[#D3CFC0] pb-3 mb-8">More from {article.category}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {related.map((a, i) => <ArticleCard key={a.slug} article={a} testId={`related-article-${i}`} />)}
          </div>
        </section>
      )}
    </article>
  );
}
